import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { db } from "../../firebaseConfig";
import AccountingShell from "./AccountingShell";

const INR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const N = (v) => Number(v || 0);
const T = (v = "") => String(v || "").toUpperCase();

const fmtDate = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime())
      ? d.toLocaleDateString("en-IN")
      : "—";
  } catch {
    return "—";
  }
};

const getDateValue = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  } catch {
    return 0;
  }
};

const getFY = (v) => {
  const d = v?.toDate ? v.toDate() : v ? new Date(v) : new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 4 ? y : y - 1;
  return `${String(start).slice(-2)}${String(start + 1).slice(-2)}`;
};

const fyStartDate = (fy) => {
  if (!fy || fy === "ALL") return 0;
  const startYear = Number(`20${String(fy).slice(0, 2)}`);
  return new Date(startYear, 3, 1).getTime();
};

const fyEndDate = (fy) => {
  if (!fy || fy === "ALL") return 9999999999999;
  const startYear = Number(`20${String(fy).slice(0, 2)}`);
  return new Date(startYear + 1, 2, 31, 23, 59, 59, 999).getTime();
};

const voucherTail = (v = "") => {
  const parts = String(v || "").split("/");
  return parts[parts.length - 1] || "";
};

const cleanText = (v = "") =>
  String(v || "")
    .replace(/\s+/g, " ")
    .replace(/Ref:\s*trf/gi, "")
    .replace(/BG Ref:\s*trf/gi, "")
    .trim();

function voucherType(type = "") {
  const t = T(type);

  if (t === "RA_BILL_INVOICE") return "RA Bill / Contractor Invoice";
  if (t === "RA_BILL_BULK_PAYMENT") return "RA Bill Payment";
  if (t === "RA_BILL_PAYMENT") return "RA Bill Payment";
  if (t === "RA_BILL_FINAL_PENALTY_CUT") return "RA Final Penalty Cut";
  if (t === "TENDER_FEE_INVOICE") return "Tender Fee Invoice";
  if (t === "TENDER_FEE_PAYMENT_RECEIVED") return "Tender Fee Payment Received";
  if (t === "REGISTRATION_GST_INVOICE") return "Registration GST Invoice";
  if (t === "REGISTRATION_PAYMENT_RECEIVED") return "Registration Payment Received";
  if (t === "VENDOR_REGISTRATION_FEE") return "Vendor Registration Fee";
  if (t === "EMD_RECEIVED_HELD") return "EMD Received / Held";
  if (t === "EMD_CONVERTED_TO_BG_SECURITY") return "BG Security Held";
  if (t === "BG_SECURITY_HELD") return "BG Security Held";
  if (t === "EMD_REFUND") return "EMD Refund";

  if (t.includes("RA_BILL")) return "RA Bill";
  if (t.includes("PAYMENT")) return "Payment";
  if (t.includes("INVOICE")) return "Invoice";
  if (t.includes("EMD")) return "EMD";
  if (t.includes("BG")) return "BG";

  return type || "Voucher";
}

function isInvoiceEntry(r) {
  const t = T(r.type);
  return t.includes("INVOICE") || t === "TENDER_FEE_INVOICE";
}

function isBulkRaPayment(r) {
  return T(r.type) === "RA_BILL_BULK_PAYMENT";
}

function isRegistrationInvoice(r) {
  return T(r.type) === "REGISTRATION_GST_INVOICE";
}

function isRegistrationPayment(r) {
  return T(r.type) === "REGISTRATION_PAYMENT_RECEIVED";
}

function isLegacyRegistrationFee(r) {
  const t = T(r.type);
  return t === "VENDOR_REGISTRATION_FEE" || t === "REGISTRATION_FEE";
}

function normalizeInvoiceNo(v = "") {
  return String(v || "")
    .replace("-PAY", "")
    .replace("PAY-", "")
    .replace("PAY_", "")
    .trim();
}

function ledgerDate(r) {
  return (
    r.invoiceDate ||
    r.paymentDate ||
    r.voucherDate ||
    r.penaltyDate ||
    r.tenderFeeInvoiceDate ||
    r.registrationInvoiceDate ||
    r.raInvoiceDate ||
    r.raBillDate ||
    r.raDate ||
    r.paymentReceiptDate ||
    r.tenderFeeReceiptDate ||
    r.registrationPaymentReceiptDate ||
    r.emdPaymentDate ||
    r.emdBgDate ||
    r.bgDate ||
    r.emdRefundDate ||
    r.createdAt
  );
}

function balanceLabel(amount) {
  if (amount > 0) return `${INR(amount)} Dr`;
  if (amount < 0) return `${INR(Math.abs(amount))} Cr`;
  return "₹ 0.00";
}

function getBaseAmount(r) {
  return N(
    r.amount ||
      r.emdAmount ||
      r.bgAmount ||
      r.refundAmount ||
      r.emdRefundAmount ||
      r.netPayable ||
      r.grossAmount ||
      r.paymentAmount ||
      r.credit ||
      r.debit ||
      0
  );
}

function getTaxable(r) {
  if (r.taxableAmount) return N(r.taxableAmount);
  if (r.approvedBasicAfterL1) return N(r.approvedBasicAfterL1);
  if (r.originalBasicAmount) return N(r.originalBasicAmount);
  if (r.grossAmount) return Number((N(r.grossAmount) / 1.18).toFixed(2));
  if (r.debit) return Number((N(r.debit) / 1.18).toFixed(2));
  if (r.credit) return Number((N(r.credit) / 1.18).toFixed(2));
  return 0;
}

function getGst(r) {
  if (r.gstAmount) return N(r.gstAmount);
  const taxable = getTaxable(r);
  const gross = N(r.grossAmount || r.debit || r.credit || 0);
  return Number((gross - taxable).toFixed(2));
}

function getGross(r) {
  return N(r.grossAmount || r.netPayable || r.debit || r.credit || 0);
}

function getPartyName(r) {
  if (isBulkRaPayment(r) && Array.isArray(r.allocations) && r.allocations.length) {
    const names = [
      ...new Set(r.allocations.map((a) => a.contractorName).filter(Boolean)),
    ];
    if (names.length === 1) return names[0];
    return `Multiple Contractors (${names.length})`;
  }

  return r.vendorName || r.contractorName || r.vendorUid || "—";
}

function getTenderName(r) {
  if (r.pacsName) return r.pacsName;
  if (r.projectName) return r.projectName;
  if (r.tenderTitle) return r.tenderTitle;
  if (r.tenderId) return r.tenderId;

  if (isBulkRaPayment(r) && Array.isArray(r.allocations) && r.allocations.length) {
    const tenders = [
      ...new Set(
        r.allocations
          .map((a) => a.pacsName || a.projectName || a.tenderTitle || a.tenderId)
          .filter(Boolean)
      ),
    ];
    if (tenders.length === 1) return tenders[0];
    return `Multiple Tenders (${tenders.length})`;
  }

  return "—";
}

function extractFullProjectLine(text = "") {
  const s = cleanText(text);
  if (!s) return "";

  const m = s.match(/(Construction of .*?(?:PMC Agency\)|PMC Agency|$))/i);
  if (m) return cleanText(m[1]);

  return "";
}

function buildFullProjectDetail(primary = {}, fallback = {}) {
  const source =
    primary.fullProjectDetail ||
    primary.projectFullDetail ||
    primary.projectDetail ||
    primary.workDetail ||
    primary.projectDescription ||
    primary.narration ||
    fallback.fullProjectDetail ||
    fallback.projectFullDetail ||
    fallback.projectDetail ||
    fallback.workDetail ||
    fallback.projectDescription ||
    fallback.narration ||
    "";

  const extracted = extractFullProjectLine(source);
  if (extracted) return extracted;

  const pacs =
    primary.pacsName ||
    fallback.pacsName ||
    primary.projectName ||
    fallback.projectName ||
    primary.tenderTitle ||
    fallback.tenderTitle ||
    "";

  const tenderId = primary.tenderId || fallback.tenderId || "";

  if (pacs && pacs !== tenderId) {
    return `Construction of 1000 MT Godown under “The World’s Largest Grain Storage Project” Scheme for ${pacs} (PACS), executed through Shine Agri Allied & Marketing Multistate Cooperative Society Limited (PMC Agency)`;
  }

  return "Project detail not available";
}

function getProjectDetail(r) {
  return (
    r.fullProjectDetail ||
    r.projectFullDetail ||
    r.projectDetail ||
    buildFullProjectDetail(r)
  );
}

function isEmdRow(r) {
  const t = T(r.type);
  return (
    t === "EMD_RECEIVED_HELD" ||
    t === "BG_SECURITY_HELD" ||
    t === "EMD_CONVERTED_TO_BG_SECURITY" ||
    t === "EMD_REFUND"
  );
}

function isBgRow(r) {
  const t = T(r.type);
  return t === "BG_SECURITY_HELD" || t === "EMD_CONVERTED_TO_BG_SECURITY";
}

function getDisplayAmounts(r) {
  const t = T(r.type);
  const amt = getBaseAmount(r);

  if (t === "RA_BILL_INVOICE") return { debit: 0, credit: amt };

  if (
    t === "RA_BILL_BULK_PAYMENT" ||
    t === "RA_BILL_PAYMENT" ||
    t === "RA_BILL_FINAL_PENALTY_CUT"
  ) {
    return { debit: amt, credit: 0 };
  }

  if (
    t === "TENDER_FEE_INVOICE" ||
    t === "REGISTRATION_GST_INVOICE" ||
    t === "VENDOR_REGISTRATION_FEE"
  ) {
    return { debit: amt, credit: 0 };
  }

  if (t === "TENDER_FEE_PAYMENT_RECEIVED" || t === "REGISTRATION_PAYMENT_RECEIVED") {
    return { debit: 0, credit: amt };
  }

  if (t === "EMD_RECEIVED_HELD") return { debit: 0, credit: amt };

  if (t === "BG_SECURITY_HELD" || t === "EMD_CONVERTED_TO_BG_SECURITY") {
    return { debit: 0, credit: amt };
  }

  if (t === "EMD_REFUND") return { debit: amt, credit: 0 };

  return { debit: N(r.debit), credit: N(r.credit) };
}

function allocationSearchText(r) {
  if (!Array.isArray(r.allocations)) return "";

  return r.allocations
    .map((a) =>
      [
        a.invoiceNo,
        a.contractorName,
        a.contractorEmail,
        a.vendorUid,
        a.tenderId,
        a.pacsName,
        a.projectName,
        a.tenderTitle,
        a.projectDetail,
        a.status,
      ].join(" ")
    )
    .join(" ");
}

function duplicateKey(r) {
  return [
    r.vendorUid || r.vendorName || r.contractorName || "BULK",
    r.tenderId || "BULK",
    r.applicationId || r.raBillId || r.billId || "NA",
    r.type || "NA",
    normalizeInvoiceNo(r.invoiceNo || r.voucherNo || r.receiptNo || r.referenceNo || "NA"),
  ].join("__");
}

function exportCSV(filename, rows) {
  if (!rows.length) return alert("No data to export");

  const header = Object.keys(rows[0]).join(",");
  const body = rows
    .map((r) =>
      Object.values(r)
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");

  const blob = new Blob([header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ContractorLedger() {
  return (
    <AccountingShell title="Contractor Ledger">
      <ContractorLedgerContent />
    </AccountingShell>
  );
}

function ContractorLedgerContent({ me = {} }) {
  const [rows, setRows] = useState([]);
  const [contractor, setContractor] = useState("");
  const [tender, setTender] = useState("ALL");
  const [type, setType] = useState("ALL");
  const [fy, setFy] = useState("ALL");
  const [search, setSearch] = useState("");
  const [selectedVoucher, setSelectedVoucher] = useState(null);

  const load = async () => {
    const snap = await getDocs(collection(db, "contractor_ledgers"));
    setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    load();
  }, []);

  const cleanRows = useMemo(() => {
    const registrationInvoiceVendorSet = new Set();

    rows.forEach((r) => {
      if (isRegistrationInvoice(r) || isRegistrationPayment(r)) {
        registrationInvoiceVendorSet.add(r.vendorUid || r.vendorName || r.contractorName);
      }
    });

    const bulkPaidKeys = new Set();

    rows.forEach((r) => {
      if (isBulkRaPayment(r) && Array.isArray(r.allocations)) {
        r.allocations.forEach((a) => {
          bulkPaidKeys.add(
            [
              r.tenderId || a.tenderId || "",
              a.raBillId || r.raBillId || "",
              normalizeInvoiceNo(a.invoiceNo || r.invoiceNo || ""),
              N(a.adjustedAmount),
            ].join("__")
          );
        });
      }
    });

    const withoutLegacy = rows.filter((r) => {
      const vendorKey = r.vendorUid || r.vendorName || r.contractorName;

      if (isLegacyRegistrationFee(r) && registrationInvoiceVendorSet.has(vendorKey)) {
        return false;
      }

      if (T(r.type) === "RA_BILL_PAYMENT") {
        const key = [
          r.tenderId || "",
          r.raBillId || "",
          normalizeInvoiceNo(r.invoiceNo || r.adjustedAgainstInvoice || r.voucherNo || ""),
          N(r.credit || r.paymentAmount || r.grossAmount || r.debit || 0),
        ].join("__");

        if (bulkPaidKeys.has(key)) return false;
      }

      return true;
    });

    const map = new Map();

    withoutLegacy.forEach((r) => {
      const key = duplicateKey(r);
      const existing = map.get(key);

      if (!existing) {
        map.set(key, { ...r, duplicateCount: 1, duplicateIds: [r.id] });
        return;
      }

      const existingDate = getDateValue(existing.updatedAt || existing.createdAt);
      const currentDate = getDateValue(r.updatedAt || r.createdAt);

      if (currentDate >= existingDate) {
        map.set(key, {
          ...r,
          duplicateCount: N(existing.duplicateCount || 1) + 1,
          duplicateIds: [...(existing.duplicateIds || []), r.id],
        });
      } else {
        map.set(key, {
          ...existing,
          duplicateCount: N(existing.duplicateCount || 1) + 1,
          duplicateIds: [...(existing.duplicateIds || []), r.id],
        });
      }
    });

    return Array.from(map.values());
  }, [rows]);

  const contractors = useMemo(() => {
    const set = new Set();

    cleanRows.forEach((r) => {
      if (isBulkRaPayment(r) && Array.isArray(r.allocations)) {
        r.allocations.forEach((a) => {
          if (a.contractorName) set.add(a.contractorName);
        });
      } else {
        set.add(r.vendorName || r.contractorName || r.vendorUid || "Unknown");
      }
    });

    return Array.from(set).filter(Boolean).sort();
  }, [cleanRows]);

  const tenders = useMemo(() => {
    const set = new Set();

    cleanRows.forEach((r) => {
      if (r.tenderId) set.add(r.tenderId);
      if (r.pacsName) set.add(r.pacsName);
      if (r.projectName) set.add(r.projectName);

      if (isBulkRaPayment(r) && Array.isArray(r.allocations)) {
        r.allocations.forEach((a) => {
          if (a.tenderId) set.add(a.tenderId);
          if (a.pacsName) set.add(a.pacsName);
          if (a.projectName) set.add(a.projectName);
        });
      }
    });

    return ["ALL", ...Array.from(set).sort()];
  }, [cleanRows]);

  const types = useMemo(() => {
    const set = new Set();
    cleanRows.forEach((r) => {
      if (r.type) set.add(r.type);
    });
    return ["ALL", ...Array.from(set).sort()];
  }, [cleanRows]);

  const fyOptions = useMemo(() => {
    const set = new Set();
    cleanRows.forEach((r) => {
      const d = ledgerDate(r);
      if (d) set.add(getFY(d));
    });
    return ["ALL", ...Array.from(set).sort().reverse()];
  }, [cleanRows]);
    const findBaseEmdRow = (r) => {
    const tail = voucherTail(r.voucherNo || r.referenceNo || "");

    const sameApp = cleanRows.find(
      (x) =>
        T(x.type) === "EMD_RECEIVED_HELD" &&
        r.applicationId &&
        x.applicationId === r.applicationId
    );
    if (sameApp) return sameApp;

    const sameTail = cleanRows.find((x) => {
      const xTail = voucherTail(x.voucherNo || x.referenceNo || "");
      return T(x.type) === "EMD_RECEIVED_HELD" && tail && xTail === tail;
    });
    if (sameTail) return sameTail;

    const sameTender = cleanRows.find(
      (x) =>
        T(x.type) === "EMD_RECEIVED_HELD" &&
        r.tenderId &&
        x.tenderId === r.tenderId &&
        (x.vendorUid === r.vendorUid ||
          x.vendorName === r.vendorName ||
          x.contractorName === r.contractorName)
    );
    if (sameTender) return sameTender;

    return {};
  };

  const fixedRows = useMemo(() => {
    const getEmdGroupKey = (r) => {
      const tail = voucherTail(r.voucherNo || r.referenceNo || "");
      return [
        tail || r.applicationId || "",
        r.tenderId || "",
        r.vendorUid || r.vendorName || r.contractorName || "",
      ].join("__");
    };

    const groups = new Map();

    cleanRows.forEach((r) => {
      if (!isEmdRow(r)) return;
      const key = getEmdGroupKey(r);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    });

    const hiddenIds = new Set();

    groups.forEach((group) => {
      const hasBG = group.some((r) => isBgRow(r));
      const hasRefund = group.some((r) => T(r.type) === "EMD_REFUND");

      if (hasBG) {
        group.forEach((r) => {
          if (T(r.type) === "EMD_RECEIVED_HELD") hiddenIds.add(r.id);
        });
      }

      if (hasRefund) {
        group.forEach((r) => {
          if (isBgRow(r)) hiddenIds.add(r.id);
        });
      }
    });

    return cleanRows
      .filter((r) => !hiddenIds.has(r.id))
      .map((r) => {
        if (isBgRow(r)) {
          const base = findBaseEmdRow(r);
          const fullProjectDetail = buildFullProjectDetail(base, r);

          const amount =
            r.amount ||
            r.bgAmount ||
            r.emdAmount ||
            r.credit ||
            base.amount ||
            base.emdAmount ||
            base.credit ||
            0;

          return {
            ...base,
            ...r,
            type: "BG_SECURITY_HELD",
            amount,
            bgAmount: amount,
            emdAmount: amount,
            debit: 0,
            credit: amount,
            fullProjectDetail,
            projectDetail: fullProjectDetail,
            pacsName: r.pacsName || base.pacsName || "",
            projectName: r.projectName || base.projectName || "",
            tenderTitle: r.tenderTitle || base.tenderTitle || "",
            tenderId: r.tenderId || base.tenderId || "",
            applicationId: r.applicationId || base.applicationId || "",
            narration:
              fullProjectDetail === "Project detail not available"
                ? `BG Security held against EMD. BG Ref: ${
                    r.referenceNo || r.emdBgReference || "—"
                  }`
                : `BG Security held against EMD for ${fullProjectDetail}. BG Ref: ${
                    r.referenceNo || r.emdBgReference || "—"
                  }`,
          };
        }

        if (T(r.type) === "EMD_REFUND") {
          const base = findBaseEmdRow(r);
          const fullProjectDetail = buildFullProjectDetail(base, r);

          const amount =
            r.amount ||
            r.refundAmount ||
            r.emdRefundAmount ||
            r.debit ||
            base.amount ||
            base.emdAmount ||
            base.credit ||
            0;

          return {
            ...base,
            ...r,
            type: "EMD_REFUND",
            amount,
            refundAmount: amount,
            emdRefundAmount: amount,
            debit: amount,
            credit: 0,
            fullProjectDetail,
            projectDetail: fullProjectDetail,
            pacsName: r.pacsName || base.pacsName || "",
            projectName: r.projectName || base.projectName || "",
            tenderTitle: r.tenderTitle || base.tenderTitle || "",
            tenderId: r.tenderId || base.tenderId || "",
            applicationId: r.applicationId || base.applicationId || "",
            narration:
              fullProjectDetail === "Project detail not available"
                ? `EMD refunded to contractor. UTR: ${
                    r.paymentUtr || r.referenceNo || "—"
                  }`
                : `EMD refunded to contractor for ${fullProjectDetail}. UTR: ${
                    r.paymentUtr || r.referenceNo || "—"
                  }`,
          };
        }

        if (T(r.type) === "EMD_RECEIVED_HELD") {
          const fullProjectDetail = buildFullProjectDetail(r);
          return {
            ...r,
            fullProjectDetail,
            projectDetail: fullProjectDetail,
            narration:
              r.narration && r.narration.includes("Construction of")
                ? cleanText(r.narration)
                : `EMD received and held as refundable security for ${fullProjectDetail}`,
          };
        }

        return {
          ...r,
          fullProjectDetail:
            r.fullProjectDetail || r.projectDetail || buildFullProjectDetail(r),
          projectDetail:
            r.projectDetail || r.fullProjectDetail || buildFullProjectDetail(r),
        };
      });
  }, [cleanRows]);

  const filtered = useMemo(() => {
    if (!contractor) return [];

    let list = [...fixedRows];

    list = list.filter((r) => {
      if (isBulkRaPayment(r) && Array.isArray(r.allocations)) {
        return r.allocations.some((a) => a.contractorName === contractor);
      }

      return (
        (r.vendorName || r.contractorName || r.vendorUid || "Unknown") ===
        contractor
      );
    });

    if (tender !== "ALL") {
      list = list.filter((r) => {
        if (r.tenderId === tender) return true;
        if (r.pacsName === tender) return true;
        if (r.projectName === tender) return true;
        if (r.tenderTitle === tender) return true;

        if (isBulkRaPayment(r) && Array.isArray(r.allocations)) {
          return r.allocations.some(
            (a) =>
              a.tenderId === tender ||
              a.pacsName === tender ||
              a.projectName === tender ||
              a.tenderTitle === tender
          );
        }

        return false;
      });
    }

    if (type !== "ALL") list = list.filter((r) => r.type === type);

    if (fy !== "ALL") {
      list = list.filter((r) => {
        const d = getDateValue(ledgerDate(r));
        return d >= fyStartDate(fy) && d <= fyEndDate(fy);
      });
    }

    if (search.trim()) {
      const s = search.toLowerCase();

      list = list.filter((r) =>
        [
          r.vendorName,
          r.contractorName,
          r.vendorUid,
          r.tenderId,
          r.tenderTitle,
          r.projectName,
          r.pacsName,
          r.projectDetail,
          r.fullProjectDetail,
          r.type,
          voucherType(r.type),
          r.voucherNo,
          r.billNo,
          r.referenceNo,
          r.narration,
          r.paymentUtr,
          r.invoiceNo,
          r.receiptNo,
          r.paymentReceiptNo,
          allocationSearchText(r),
        ]
          .join(" ")
          .toLowerCase()
          .includes(s)
      );
    }

    list.sort((a, b) => getDateValue(ledgerDate(a)) - getDateValue(ledgerDate(b)));

    const opening =
      fy === "ALL"
        ? 0
        : fixedRows
            .filter((r) => {
              const name =
                r.vendorName || r.contractorName || r.vendorUid || "Unknown";
              return name === contractor && getDateValue(ledgerDate(r)) < fyStartDate(fy);
            })
            .reduce((s, r) => {
              const { debit, credit } = getDisplayAmounts(r);
              return s + debit - credit;
            }, 0);

    let running = opening;

    return list.map((r) => {
      const { debit, credit } = getDisplayAmounts(r);
      running += debit - credit;

      return {
        ...r,
        displayDebit: debit,
        displayCredit: credit,
        runningBalance: running,
      };
    });
  }, [fixedRows, contractor, tender, type, fy, search]);

  const openingBalance = useMemo(() => {
    if (!contractor || fy === "ALL") return 0;

    return fixedRows
      .filter((r) => {
        const name = r.vendorName || r.contractorName || r.vendorUid || "Unknown";
        return name === contractor && getDateValue(ledgerDate(r)) < fyStartDate(fy);
      })
      .reduce((s, r) => {
        const { debit, credit } = getDisplayAmounts(r);
        return s + debit - credit;
      }, 0);
  }, [fixedRows, contractor, fy]);

  const totals = useMemo(() => {
    const debit = filtered.reduce((s, r) => s + N(r.displayDebit), 0);
    const credit = filtered.reduce((s, r) => s + N(r.displayCredit), 0);
    const balance = openingBalance + debit - credit;

    return { debit, credit, balance };
  }, [filtered, openingBalance]);

  const sendCorrection = async (r) => {
    const remark = window.prompt("Correction remark likho:", r.correctionRemark || "");
    if (!remark) return;

    await updateDoc(doc(db, "contractor_ledgers", r.id), {
      ledgerCorrectionStatus: "CORRECTION_REQUIRED",
      correctionRemark: remark,
      correctionRaisedBy: me.uid || "",
      correctionRaisedAt: serverTimestamp(),
      status: "CORRECTION_REQUIRED",
    });

    alert("Correction Accountant ko bhej diya.");
    load();
  };

  const downloadLedger = () => {
    exportCSV(
      "contractor_ledger_tally_style.csv",
      filtered.map((r, i) => ({
        Sr: i + 1,
        Date: fmtDate(ledgerDate(r)),
        VoucherNo: r.voucherNo || r.billNo || r.referenceNo || r.id,
        VoucherType: voucherType(r.type),
        Contractor: getPartyName(r),
        Tender: getTenderName(r),
        ProjectDetail: r.fullProjectDetail || r.projectDetail || getProjectDetail(r),
        Particulars: r.narration || "—",
        Debit: r.displayDebit,
        Credit: r.displayCredit,
        Balance: balanceLabel(r.runningBalance),
      }))
    );
  };

  const printInvoice = (r) => {
    const w = window.open("", "_blank");
    if (!w) return;

    w.document.write(`
      <html>
        <head>
          <title>${r.voucherNo || r.invoiceNo || "Voucher"}</title>
          <style>
            body{font-family:Arial,sans-serif;padding:30px;color:#0f172a}
            .head{text-align:center;border-bottom:2px solid #0f172a;padding-bottom:12px}
            h1{font-size:22px;margin:0}
            h2{font-size:16px;margin:10px 0 0}
            table{width:100%;border-collapse:collapse;margin-top:22px}
            td,th{border:1px solid #cbd5e1;padding:10px;font-size:13px}
            th{background:#0f172a;color:white;text-align:left}
          </style>
        </head>
        <body>
          <div class="head">
            <h1>Shine Agri Allied & Marketing Multistate Cooperative Society Ltd.</h1>
            <h2>${voucherType(r.type)}</h2>
            <div>Voucher No: ${
              r.voucherNo || r.invoiceNo || r.id
            } | Date: ${fmtDate(ledgerDate(r))}</div>
          </div>
          <table>
            <tr><th>Contractor</th><td>${getPartyName(r)}</td></tr>
            <tr><th>Tender / Project</th><td>${getTenderName(r)}</td></tr>
            <tr><th>Project Detail</th><td>${
              r.fullProjectDetail || r.projectDetail || getProjectDetail(r)
            }</td></tr>
            <tr><th>Narration</th><td>${r.narration || "—"}</td></tr>
            <tr><th>Reference</th><td>${r.referenceNo || r.paymentUtr || "—"}</td></tr>
            <tr><th>Debit</th><td>${r.displayDebit ? INR(r.displayDebit) : "—"}</td></tr>
            <tr><th>Credit</th><td>${r.displayCredit ? INR(r.displayCredit) : "—"}</td></tr>
            <tr><th>Running Balance</th><td>${balanceLabel(r.runningBalance)}</td></tr>
          </table>
          <script>window.print()</script>
        </body>
      </html>
    `);

    w.document.close();
  };

  const downloadLedgerPDF = () => {
    if (!filtered.length) return alert("No data to export");

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text("Shine Agri Allied & Marketing Multistate Cooperative Society Ltd.", 148, 14, {
      align: "center",
    });

    pdf.setFontSize(11);
    pdf.text("Contractor Ledger Account", 148, 22, { align: "center" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.text(
      `Contractor: ${contractor || "Select Contractor"} | FY: ${
        fy === "ALL" ? "All Financial Years" : `FY ${fy}`
      } | Opening: ${balanceLabel(openingBalance)}`,
      148,
      29,
      { align: "center" }
    );

    autoTable(pdf, {
      startY: 36,
      margin: { left: 6, right: 6 },
      tableWidth: "auto",
      head: [["Sr", "Date", "Voucher No", "Type", "Project", "Particulars", "Debit", "Credit", "Balance"]],
      body: filtered.map((r, i) => [
        i + 1,
        fmtDate(ledgerDate(r)),
        r.voucherNo || r.billNo || r.referenceNo || r.id,
        voucherType(r.type),
        getTenderName(r),
        String(r.narration || "—")
          .replace(/₹/g, "Rs.")
          .replace(/[^\x20-\x7E\n]/g, " "),
        r.displayDebit ? INR(r.displayDebit) : "—",
        r.displayCredit ? INR(r.displayCredit) : "—",
        balanceLabel(r.runningBalance),
      ]),
      foot: [
        [
          "",
          "",
          "",
          "",
          "",
          "Closing Balance / Total",
          INR(totals.debit),
          INR(totals.credit),
          balanceLabel(totals.balance),
        ],
      ],
      theme: "grid",
      styles: {
        font: "helvetica",
        fontSize: 6.2,
        cellPadding: 1.4,
        overflow: "linebreak",
        valign: "top",
      },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold" },
      footStyles: {
        fillColor: [241, 245, 249],
        textColor: [15, 23, 42],
        fontStyle: "bold",
      },
    });

    pdf.save("Contractor_Ledger_Landscape.pdf");
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 p-5 text-white shadow-lg print:hidden">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-black">Contractor Ledger</h2>
            <p className="text-xs text-slate-300 mt-1">
              Tally-style contractor account with financial year, opening balance and closing balance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 w-full lg:w-auto lg:min-w-[900px]">
            <FilterSelect
              label="Contractor"
              value={contractor}
              setValue={setContractor}
              options={["", ...contractors]}
              allText="Select Contractor"
              dark
            />

            <FilterSelect
              label="Financial Year"
              value={fy}
              setValue={setFy}
              options={fyOptions}
              allText="All Years"
              dark
            />

            <FilterSelect
              label="Project"
              value={tender}
              setValue={setTender}
              options={tenders}
              allText="All Projects"
              dark
            />

            <div>
              <label className="label-dark">Voucher Type</label>
              <select value={type} onChange={(e) => setType(e.target.value)} className="input-dark">
                {types.map((t) => (
                  <option key={t} value={t}>
                    {t === "ALL" ? "All Types" : voucherType(t)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label-dark">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Voucher / project"
                className="input-dark"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={downloadLedger} className="btn-white">Download CSV</button>
          <button onClick={downloadLedgerPDF} className="btn-white">Download PDF</button>
          <button onClick={() => window.print()} className="btn-white">Print</button>
          <button
            onClick={() => {
              setContractor("");
              setTender("ALL");
              setType("ALL");
              setFy("ALL");
              setSearch("");
            }}
            className="btn-reset"
          >
            Reset
          </button>
        </div>
      </div>

      {!contractor ? (
        <div className="bg-white rounded-3xl border p-12 text-center shadow-sm">
          <div className="mx-auto h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center text-2xl">
            📒
          </div>
          <h2 className="text-2xl font-black text-slate-900 mt-4">Select Contractor</h2>
          <p className="text-sm text-slate-500 mt-2">
            Contractor select karte hi uska complete ledger opening balance ke saath show hoga.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 print:hidden">
            <Card title="Opening Balance" value={balanceLabel(openingBalance)} balance={openingBalance} />
            <Card title="Total Debit" value={INR(totals.debit)} balance={totals.debit} />
            <Card title="Total Credit" value={INR(totals.credit)} balance={-totals.credit} />
            <Card title="Closing Balance" value={balanceLabel(totals.balance)} balance={totals.balance} />
          </div>

          <div className="bg-white rounded-3xl border p-5 shadow-sm">
            <div className="mb-5 text-center">
              <h2 className="text-xl font-black text-slate-900">
                Shine Agri Allied & Marketing Multistate Cooperative Society Ltd.
              </h2>
              <p className="text-sm text-slate-500">Contractor Ledger Account</p>
              <p className="text-xs text-slate-500">
                Contractor: <b>{contractor}</b> | FY:{" "}
                <b>{fy === "ALL" ? "All Financial Years" : `FY ${fy}`}</b> | Opening:{" "}
                <b>{balanceLabel(openingBalance)}</b>
              </p>
            </div>

            <div className="overflow-auto">
              <table className="min-w-[1550px] w-full text-xs border border-slate-300">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="p-3 border text-center">Sr</th>
                    <th className="p-3 border text-center">Date</th>
                    <th className="p-3 border text-left">Voucher No</th>
                    <th className="p-3 border text-left">Voucher Type</th>
                    <th className="p-3 border text-left">Project</th>
                    <th className="p-3 border text-left">Particulars</th>
                    <th className="p-3 border text-right">Debit</th>
                    <th className="p-3 border text-right">Credit</th>
                    <th className="p-3 border text-right">Balance</th>
                    <th className="p-3 border text-center print:hidden">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {fy !== "ALL" && (
                    <tr className="bg-blue-50 font-black">
                      <td colSpan={8} className="p-3 border text-right">
                        Opening Balance
                      </td>
                      <td className="p-3 border text-right">{balanceLabel(openingBalance)}</td>
                      <td className="p-3 border print:hidden"></td>
                    </tr>
                  )}

                  {filtered.map((r, i) => {
                    const invoice = isInvoiceEntry(r);
                    const bulk = isBulkRaPayment(r);
                    const t = T(r.type);
                    const fullDetail = r.fullProjectDetail || r.projectDetail || getProjectDetail(r);

                    return (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="p-3 border text-center">{i + 1}</td>
                        <td className="p-3 border text-center">{fmtDate(ledgerDate(r))}</td>
                        <td className="p-3 border font-black">{r.voucherNo || r.billNo || r.referenceNo || r.id}</td>
                        <td className="p-3 border font-bold">{voucherType(r.type)}</td>
                        <td className="p-3 border font-black">{getTenderName(r)}</td>

                        <td className="p-3 border">
                          <div className="font-semibold text-slate-900 leading-relaxed">
                            {cleanText(r.narration) || "—"}
                          </div>

                          {(t === "EMD_RECEIVED_HELD" || t === "BG_SECURITY_HELD" || t === "EMD_REFUND") &&
                            fullDetail !== "Project detail not available" && (
                              <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                <div className="text-[10px] font-black text-slate-500 uppercase tracking-wide">
                                  Project Detail
                                </div>
                                <div className="text-[11px] text-slate-700 leading-relaxed mt-1">
                                  {fullDetail}
                                </div>
                              </div>
                            )}

                          {invoice && !bulk && (
                            <div className="text-[10px] text-slate-500 mt-1">
                              Taxable: {INR(getTaxable(r))} | GST: {INR(getGst(r))} | Gross/Net: {INR(getGross(r))}
                            </div>
                          )}

                          {bulk && Array.isArray(r.allocations) && (
                            <div className="mt-2 border rounded-xl overflow-hidden">
                              <table className="w-full text-[10px]">
                                <thead className="bg-slate-100">
                                  <tr>
                                    <th className="p-1 border text-left">Invoice</th>
                                    <th className="p-1 border text-left">Contractor</th>
                                    <th className="p-1 border text-right">Paid</th>
                                    <th className="p-1 border text-right">Balance</th>
                                    <th className="p-1 border text-center">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.allocations.map((a, idx) => (
                                    <tr key={idx}>
                                      <td className="p-1 border font-bold">{a.invoiceNo || "—"}</td>
                                      <td className="p-1 border">{a.contractorName || "—"}</td>
                                      <td className="p-1 border text-right text-emerald-700 font-black">
                                        {INR(a.adjustedAmount)}
                                      </td>
                                      <td className="p-1 border text-right">{INR(a.outstandingAfter)}</td>
                                      <td className="p-1 border text-center">{a.status || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>

                        <td className="p-3 border text-right font-black text-red-600">
                          {r.displayDebit ? INR(r.displayDebit) : "—"}
                        </td>
                        <td className="p-3 border text-right font-black text-emerald-700">
                          {r.displayCredit ? INR(r.displayCredit) : "—"}
                        </td>
                        <td className="p-3 border text-right font-black">{balanceLabel(r.runningBalance)}</td>

                        <td className="p-3 border text-center print:hidden">
                          <div className="flex flex-wrap gap-2 justify-center">
                            <button className="btn-gray" onClick={() => setSelectedVoucher(r)}>Detail</button>
                            {(invoice || bulk) && (
                              <button className="btn-mini" onClick={() => printInvoice(r)}>Print</button>
                            )}
                            <button className="btn-red" onClick={() => sendCorrection(r)}>Correction</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!filtered.length && (
                    <tr>
                      <td colSpan={10} className="p-8 text-center text-slate-500">
                        No ledger entries found.
                      </td>
                    </tr>
                  )}

                  {filtered.length > 0 && (
                    <tr className="bg-slate-100 font-black">
                      <td colSpan={6} className="p-3 border text-right">Closing Balance / Total</td>
                      <td className="p-3 border text-right text-red-600">{INR(totals.debit)}</td>
                      <td className="p-3 border text-right text-emerald-700">{INR(totals.credit)}</td>
                      <td className="p-3 border text-right">{balanceLabel(totals.balance)}</td>
                      <td className="p-3 border print:hidden"></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {selectedVoucher && (
        <VoucherDetailModal
          row={selectedVoucher}
          onClose={() => setSelectedVoucher(null)}
          onPrint={() => printInvoice(selectedVoucher)}
        />
      )}

      <style>{`
        .label-dark{font-size:11px;font-weight:900;color:#cbd5e1}
        .input-dark{margin-top:4px;width:100%;border:1px solid rgba(255,255,255,.22);background:rgba(255,255,255,.08);color:white;border-radius:14px;padding:10px 12px;font-size:12px;font-weight:800;outline:none}
        .input-dark option{color:#0f172a}
        .btn-white{background:white;color:#0f172a;padding:8px 13px;border-radius:12px;font-size:11px;font-weight:900}
        .btn-reset{background:rgba(255,255,255,.12);color:white;padding:8px 13px;border-radius:12px;font-size:11px;font-weight:900;border:1px solid rgba(255,255,255,.2)}
        .btn{background:#059669;color:white;padding:8px 12px;border-radius:10px;font-size:11px;font-weight:800}
        .btn-dark{background:#0f172a;color:white;padding:8px 12px;border-radius:10px;font-size:11px;font-weight:800}
        .btn-red{background:#dc2626;color:white;padding:8px 12px;border-radius:10px;font-size:11px;font-weight:800}
        .btn-gray{background:#e2e8f0;color:#0f172a;padding:8px 12px;border-radius:10px;font-size:11px;font-weight:800}
        .btn-mini{background:#0f172a;color:white;padding:5px 9px;border-radius:8px;font-size:10px;font-weight:800}
      `}</style>
    </div>
  );
}

function VoucherDetailModal({ row, onClose, onPrint }) {
  const fullDetail = row.fullProjectDetail || row.projectDetail || getProjectDetail(row);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 print:hidden">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-auto">
        <div className="p-5 border-b flex justify-between items-center bg-slate-900 text-white rounded-t-2xl">
          <div>
            <h2 className="text-lg font-black">{voucherType(row.type)}</h2>
            <p className="text-xs text-slate-300">
              Voucher No: {row.voucherNo || row.billNo || row.referenceNo || row.id}
            </p>
          </div>
          <button onClick={onClose} className="bg-white text-slate-900 px-3 py-1 rounded-lg font-black">
            X
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <InfoCard title="Voucher Date" value={fmtDate(ledgerDate(row))} />
            <InfoCard title="Contractor" value={getPartyName(row)} />
            <InfoCard title="Project" value={getTenderName(row)} />
          </div>

          <div className="border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                <DetailRow label="Voucher Type" value={voucherType(row.type)} />
                <DetailRow label="Project Detail" value={fullDetail} />
                <DetailRow label="Narration" value={cleanText(row.narration) || "—"} />
                <DetailRow label="Reference No" value={row.referenceNo || row.paymentReference || row.paymentUtr || "—"} />
                <DetailRow label="Debit" value={row.displayDebit ? INR(row.displayDebit) : "—"} red />
                <DetailRow label="Credit" value={row.displayCredit ? INR(row.displayCredit) : "—"} green />
                <DetailRow label="Running Balance" value={balanceLabel(row.runningBalance)} />
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            {row.invoicePdfUrl && (
              <button className="btn-gray" onClick={() => window.open(row.invoicePdfUrl, "_blank")}>
                Open Invoice PDF
              </button>
            )}
            <button className="btn-dark" onClick={onPrint}>Print</button>
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, red, green }) {
  return (
    <tr>
      <td className="border p-3 bg-slate-50 font-bold w-52">{label}</td>
      <td
        className={`border p-3 font-bold ${
          red ? "text-red-600" : green ? "text-emerald-700" : "text-slate-900"
        }`}
      >
        {value}
      </td>
    </tr>
  );
}

function InfoCard({ title, value }) {
  return (
    <div className="border rounded-xl p-4">
      <p className="text-xs text-slate-500">{title}</p>
      <p className="font-black mt-1">{value}</p>
    </div>
  );
}

function FilterSelect({ label, value, setValue, options, allText, dark }) {
  return (
    <div>
      <label className={dark ? "label-dark" : "label"}>{label}</label>
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className={dark ? "input-dark" : "input"}
      >
        {options.map((o) => (
          <option key={o || "EMPTY"} value={o}>
            {o === "" ? allText : o === "ALL" ? allText : o}
          </option>
        ))}
      </select>
    </div>
  );
}

function Card({ title, value, balance }) {
  return (
    <div className="bg-white rounded-2xl border p-5 shadow-sm">
      <p className="text-xs text-slate-500">{title}</p>
      <h2
        className={`text-xl font-black mt-2 ${
          balance > 0
            ? "text-red-600"
            : balance < 0
            ? "text-emerald-700"
            : "text-slate-900"
        }`}
      >
        {value}
      </h2>
    </div>
  );
}